import api from '../api/apiClient';

export const getInventoryReports = async (orgId, startDate, endDate) => {
  try {
    const response = await api.get(`/reports/inventory/${orgId}`, {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory reports:', error);
    throw error;
  }
};

export const getSalesReports = async (orgId, startDate, endDate) => {
  try {
    const response = await api.get(`/reports/sales/${orgId}`, {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching sales reports:', error);
    throw error;
  }
};

export const getPurchaseReports = async (orgId, startDate, endDate) => {
  try {
    const response = await api.get(`/reports/purchases/${orgId}`, {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase reports:', error);
    throw error;
  }
};

export const getInventoryMovementReports = async (orgId, startDate, endDate, limit = 20) => {
  try {
    const response = await api.get(`/reports/inventory-movements/${orgId}`, {
      params: { startDate, endDate, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory movement reports:', error);
    throw error;
  }
};

export const getCustomerReports = async (orgId, startDate, endDate) => {
  try {
    const response = await api.get(`/reports/customers/${orgId}`, {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching customer reports:', error);
    throw error;
  }
};

export const getSupplierReports = async (orgId, startDate, endDate) => {
  try {
    const response = await api.get(`/reports/suppliers/${orgId}`, {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier reports:', error);
    throw error;
  }
};

export const downloadReport = async (orgId, reportType, format, startDate, endDate) => {
  try {
    const response = await api.get(`/reports/download/${orgId}/${reportType}`, {
      params: { format, startDate, endDate },
      responseType: 'blob'
    });
    
    // Create a download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${reportType}-report-${new Date().toISOString()}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return true;
  } catch (error) {
    console.error('Error downloading report:', error);
    throw error;
  }
};

// New method to get a list of organizations for the current user
export const getUserOrganizations = async () => {
  try {
    const response = await api.get('/organizations');
    return response.data;
  } catch (error) {
    console.error('Error fetching user organizations:', error);
    throw error;
  }
};

// Add caching mechanism to prevent duplicate API calls
const cache = new Map();

const getCacheKey = (endpoint, params) => {
  return `${endpoint}-${JSON.stringify(params)}`;
};

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < 300000) { // 5 minutes cache
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

export const getUniversalSalesReports = async (orgIds, startDate, endDate) => {
  const cacheKey = getCacheKey('universal-sales', { orgIds, startDate, endDate });
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    const response = await api.get('/reports/universal/sales', {
      params: { orgIds: orgIds.join(','), startDate, endDate }
    });
    
    setCachedData(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching universal sales reports:', error);
    throw error;
  }
};

export const getUniversalPurchaseReports = async (orgIds, startDate, endDate) => {
  const cacheKey = getCacheKey('universal-purchases', { orgIds, startDate, endDate });
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    const response = await api.get('/reports/universal/purchases', {
      params: { orgIds: orgIds.join(','), startDate, endDate }
    });
    
    setCachedData(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching universal purchase reports:', error);
    throw error;
  }
};

export const getUniversalInventoryReports = async (orgIds, startDate, endDate) => {
  const cacheKey = getCacheKey('universal-inventory', { orgIds, startDate, endDate });
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    const response = await api.get('/reports/universal/inventory', {
      params: { orgIds: orgIds.join(','), startDate, endDate }
    });
    
    setCachedData(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching universal inventory reports:', error);
    throw error;
  }
};

export const getUniversalCustomerReports = async (orgIds, startDate, endDate) => {
  const cacheKey = getCacheKey('universal-customers', { orgIds, startDate, endDate });
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    const response = await api.get('/reports/universal/customers', {
      params: { orgIds: orgIds.join(','), startDate, endDate }
    });
    
    setCachedData(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching universal customer reports:', error);
    throw error;
  }
};

export const getUniversalSupplierReports = async (orgIds, startDate, endDate) => {
  const cacheKey = getCacheKey('universal-suppliers', { orgIds, startDate, endDate });
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    const response = await api.get('/reports/universal/suppliers', {
      params: { orgIds: orgIds.join(','), startDate, endDate }
    });
    
    setCachedData(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching universal supplier reports:', error);
    throw error;
  }
};

// Clear cache function (call when data changes)
export const clearReportsCache = () => {
  cache.clear();
};

export async function fetchSalesReports(orgId, startDate, endDate) {
  const response = await api.get(`/reports/sales/${orgId}`, {
    params: { startDate, endDate },
  });
  // Now response.data has .unitsSoldBreakdown
  return response.data;
}

export const getRevenueReports = async (orgId, startDate, endDate) => {
  try {
    const response = await api.get(`/reports/revenue`, {
      params: { org_id: orgId, startDate, endDate }, // Use org_id as a query parameter
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching revenue reports:', error);
    throw error;
  }
};
